import type { FormActionResult } from '@/stores/form-store.types';

interface TerminalTaskSubmitHandlers {
  consumePending: () => void;
  refreshTasks: () => void;
  hasSuccessComponent: boolean;
  clearCurrentTask: () => void;
  notifySuccess: () => void;
  leaveTask: () => void;
}

type TerminalTaskSubmitResult =
  | Extract<FormActionResult, { ok: true }>
  | (Extract<FormActionResult, { ok: false }> & { reason: 'stale' });

export function handleTerminalTaskSubmitResult(
  result: FormActionResult,
  handlers: TerminalTaskSubmitHandlers,
): result is TerminalTaskSubmitResult {
  const isStale = !result.ok && result.reason === 'stale';
  if (!result.ok && !isStale) {
    return false;
  }

  handlers.consumePending();
  if (isStale) {
    return true;
  }

  handlers.refreshTasks();
  if (!handlers.hasSuccessComponent) {
    handlers.clearCurrentTask();
    handlers.notifySuccess();
    handlers.leaveTask();
  }
  return true;
}

interface RealtimeFormState {
  formRequestGeneration: number;
  loadedTaskId: string | null;
  error: string | null;
}

interface ReconcileRealtimeFormReloadOptions {
  taskId: string;
  loadForm: (taskId: string) => Promise<void>;
  getFormState: () => RealtimeFormState;
  onApplied: () => void;
  onFailed: () => void;
}

export type RealtimeFormReloadResult = 'applied' | 'failed' | 'stale';

export async function reconcileRealtimeFormReload({
  taskId,
  loadForm,
  getFormState,
  onApplied,
  onFailed,
}: ReconcileRealtimeFormReloadOptions): Promise<RealtimeFormReloadResult> {
  const reloadPromise = loadForm(taskId);
  const reloadGeneration = getFormState().formRequestGeneration;
  await reloadPromise;

  const reloadedFormState = getFormState();
  if (reloadedFormState.formRequestGeneration !== reloadGeneration) {
    return 'stale';
  }
  if (!reloadedFormState.error && reloadedFormState.loadedTaskId === taskId) {
    onApplied();
    return 'applied';
  }

  onFailed();
  return 'failed';
}
